import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import { useFormik } from "formik";
import { useHandleBack } from "helpers/functions/useHandleBack";
import { useEffect } from "react";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { BsArrowLeft } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchOrgDropDown } from "slices/department/thunk";
import * as Yup from "yup";

const DepartmentForm = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const departemnt = location.state?.departemnt;
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const handleBack = useHandleBack("/department-list")

  useEffect(() => {
    dispatch(fetchOrgDropDown());
  }, [dispatch]);

  const orgDropDown: any[] = useSelector(
    (state: any) => state.department.orgDropDown
  );
  const orgDropDownLoading = useSelector(
    (state: any) => state.department.orgDropDownLoading
  );

  const formik = useFormik({
    initialValues: {
      organization: "",
      department_name: "",
      organization_name: "",
      department_id: "",
    },
    validationSchema: Yup.object({
      organization: Yup.string().required("Organisation Name is required"),
      department_name: Yup.string()
        .required("Department Name is required")
        .min(3, "Department Name must be at least 3 characters"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          organization: values.organization,
          department_name: values.department_name,
        };

        let response;
        if (id) {
          response = await Axios.put(`api/department/edit/${id}/`, payload);
        } else {
          response = await Axios.post("api/department/create/", payload);
        }
        console.log("Response:", response);
        if (response.status === 200 || response.status === 201) {
          toast.success(`Department ${id ? "updated" : "created"} successfully`);
          setTimeout(() => {
            navigate("/department-list"); // navigate after 1.5s
          }, 1500);
          handleBack()
        }


      } catch (error: any) {
        const apiError = error?.response?.data;

        if (apiError?.errors?.non_field_errors?.length) {
          toast.error(apiError.errors.non_field_errors[0]);
        } else if (apiError?.message) {
          toast.error(apiError.message);
        } else {
          toast.error("Something went wrong");
        }
      }
    },
  });

  useEffect(() => {
    if (id && departemnt) {
      formik.setValues({
        organization: departemnt?.organization || "",
        department_name: departemnt?.department_name || "",
        organization_name: departemnt?.organization_name || "",
        department_id: id,
      });
    }
  }, [id, departemnt]);

  return (
    <div
      className="add-organisation-container p-4 rounded shadow-sm bg-white fs-6"
      style={{ marginTop: "6rem", marginLeft: "2rem", marginRight: "2rem" }}
    >
      {/* <ToastContainer /> */}
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div className="d-flex align-items-center">
          <span
            className="me-2"
            style={{ fontSize: "1.25rem", cursor: "pointer" }}
            onClick={handleBack}
          >
            <BsArrowLeft />
          </span>
          <h5 className="mb-0 fw-bold">{id ? "Edit" : "Add"} Department</h5>
        </div>
        <div className="text-muted" style={{ fontSize: "0.9rem" }}>
          <span
            onClick={handleBack}
            style={{ color: "#6c757d", cursor: "pointer" }}
          >
            Department
          </span>{" "}
          &gt;{" "}
          <span style={{ color: "#6c757d", cursor: "default" }}>
            {id ? "Edit Department" : "Add Department"}
          </span>
        </div>
      </div>

      <div className="my-4">
        <Card className="p-4 shadow-sm rounded">
          <Form noValidate onSubmit={formik.handleSubmit}>
            <Row className="mb-4">
              {/* Organisation */}
              <Col md={6} xs={12} className="mb-3 mb-md-0">
                <Form.Label className="fw-semibold">
                  Organisation Name <span className="text-danger">*</span>
                </Form.Label>
                {orgDropDownLoading ? (
                  <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading...
                  </div>
                ) : (
                <Autocomplete
  disablePortal
  options={orgDropDown}
  getOptionLabel={(option: any) => option.organization_name || ""}
  value={
    orgDropDown.find(
      (org) => org?.organization_id === formik.values.organization
    ) || null
  }
  onChange={(_, value) => {
    // Update Formik values
    formik.setFieldValue("organization", value?.organization_id || "");
    formik.setFieldValue("organization_name", value?.organization_name || "");

    // Clear validation error immediately
    formik.setFieldError("organization", "");
    formik.setFieldTouched("organization", true, false);

    // Optional: re-validate field immediately
    formik.validateField("organization");
  }}
  onBlur={() => formik.setFieldTouched("organization", true)}
  renderInput={(params) => (
    <TextField
      {...params}
      placeholder="Select Organisation"
      error={formik.touched.organization && !!formik.errors.organization}
      helperText={formik.touched.organization && formik.errors.organization}
      fullWidth
      size="small"
      autoComplete="off"
    />
  )}
/>


                )}
              </Col>

              {/* Department Name */}
              <Col md={6} xs={12}>
                <Form.Label className="fw-semibold">
                  Department Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="department_name"
                  placeholder="Enter Department Name"
                  value={formik.values.department_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.department_name &&
                    !!formik.errors.department_name
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.department_name}
                </Form.Control.Feedback>
              </Col>
            </Row>

            {/* Buttons */}
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
              <Button
                variant="outline-secondary"
                style={{
                  color: "#878899",
                  borderColor: "#B0BAC9",
                  backgroundColor: "transparent",
                }}
                type="reset"
                onClick={handleBack}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                style={{
                  backgroundColor: "#0073BB",
                  borderColor: "#0073BB",
                }}
              >
                {id ? "Update" : "Submit"}
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default DepartmentForm;